/*
 * Cubica theme behavior: small Web Components, no dependencies.
 *
 * window.Cubica (routes + strings are set inline in layout/theme.liquid)
 *   openDialog(id, trigger)       open <dialog id> as a modal (closes others)
 *   closeDialog(dialog)           close it, after its exit where the browser cannot animate a closing dialog
 *   cart.add(variantId, qty, props) → Promise<cart add response>; opens the bag
 *   cart.change(lineKey, qty)     → Promise<cart>
 *   cart.refresh()                re-render the bag section
 *   cart.get()                    → Promise</cart.js>
 *   cart.buyNow(variantId, only)  → Promise; puts the bag in shape for /checkout ("buy now"); `only`
 *                                 sets the other lines aside in localStorage cubica-saved-bag, which
 *                                 the next storefront page puts back (restoreSavedBag)
 *   burst(el, {subtle, delay, left})  the storefront's Burst firework centred in el (positioned)
 *   favorites.list() / has(handle) / toggle(handle)
 *   saveLook(look) → Promise      not defined here: a script that keeps the look on a signed-in
 *                                 customer's account adds it; the personalization page calls it
 *   audio()                       the page's one AudioContext (UI sounds and Cubica Music Radio)
 *   sound(name)                   plays a UI sound from Cubica.sounds unless cubica-sounds is "off"
 *
 * Markup hooks
 *   [data-cubica-open="dialog-id"]   opens a dialog;  [data-cubica-close] closes its dialog
 *   form[data-cubica-add]            product form posted to /cart/add.js (status in [data-status])
 *   form[data-cubica-autosubmit]     submits when a select changes (localization)
 *   [data-cubica-bag-count] [data-cubica-bag-button]   kept in sync with the bag
 *
 * Events (on document)
 *   cubica:cart:updated   detail { count, products, variants, added }
 *   cubica:cart:error     detail { message }
 *   cubica:favorites:changed  detail { handles }
 *   cubica:dialog:open    detail { id }
 *   cubica:avatar:changed detail { character, color, tint, reactions }  (personalization page)
 */
(() => {
  const C = (window.Cubica = window.Cubica || {});
  const routes = C.routes || {};
  const strings = C.strings || {};
  const BAG = 'cubica-bag';
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));
  // The storefront's character reactions (character/index.tsx react()): the 3D scene listens for this.
  const react = (kind) => window.dispatchEvent(new CustomEvent('pangea:character', { detail: kind }));
  document.addEventListener('submit', (event) => {
    if (event.submitter?.name === 'checkout') react('checkout');
  });
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const storage = {
    get(key) {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch {}
    },
    remove(key) {
      try {
        localStorage.removeItem(key);
      } catch {}
    },
  };
  const pop = (el) => {
    if (!el || reduced()) return;
    el.classList.remove('is-popping');
    void el.offsetWidth;
    el.classList.add('is-popping');
    el.addEventListener('animationend', () => el.classList.remove('is-popping'), { once: true });
  };

  /* ---------- UI sounds: SND kit 01 "sine" by Yasuhiro Tsuchiya (snd.dev) ---------- */

  // Quiet, and on until Personalización turns them off. "Ambient" lets iOS follow the silent switch and
  // mix with other audio; each play varies pitch and level a little (HIG playing-audio).
  let context;
  let buffers;
  C.audio = () => {
    if (!context) {
      try {
        navigator.audioSession.type = 'ambient';
      } catch {}
      context = new AudioContext();
    }
    if (context.state === 'suspended') context.resume();
    return context;
  };
  const decode = () =>
    (buffers ??= Object.fromEntries(
      Object.entries(C.sounds || {}).map(([name, url]) => [name, fetch(url).then((r) => r.arrayBuffer()).then((data) => C.audio().decodeAudioData(data))]),
    ));
  C.sound = async (name) => {
    if (storage.get('cubica-sounds') === 'off' || !C.sounds?.[name]) return;
    const ctx = C.audio();
    const source = new AudioBufferSourceNode(ctx, { buffer: await decode()[name], playbackRate: 0.97 + Math.random() * 0.06 });
    source.connect(new GainNode(ctx, { gain: 0.22 + Math.random() * 0.06 })).connect(ctx.destination);
    source.start();
  };
  const play = (name) => C.sound(name).catch(() => {});
  document.addEventListener('pointerdown', () => storage.get('cubica-sounds') !== 'off' && decode(), { once: true, capture: true });
  // After the click, so a switch reads its new state and Personalización has stored its choice. Adding to
  // the bag sounds when the bag answers; plain links stay silent.
  document.addEventListener('click', (event) => {
    const el = event.target.closest?.('button, [role="button"], [role="switch"], input[type="radio"], input[type="checkbox"], summary, a[href$="/checkout"]');
    if (!el || el.disabled || el.getAttribute('aria-disabled') === 'true' || el.closest('cubica-radio, form[data-cubica-add] [type="submit"]')) return;
    setTimeout(() => {
      if (el.matches('[data-buy-now], [data-buy-only], [data-buy-all], [name="checkout"], a')) play('transitionUp');
      else if (el.matches('[role="switch"]')) play(el.checked || el.getAttribute('aria-checked') === 'true' ? 'toggleOn' : 'toggleOff');
      else play(el.matches('input, [aria-pressed], [role="radio"], [role="tab"]') ? 'select' : 'button');
    });
  });
  document.addEventListener('cubica:cart:updated', (event) => event.detail?.added && play('celebration'));
  document.addEventListener('cubica:cart:error', () => play('caution'));

  /* ---------- Celebrations: components/ui/burst.tsx, hand-tuned sparks (angle, distance, delay) ---------- */

  const spark = (kind, angles, to, delay) => angles.map((angle, i) => [kind, angle, to[i], delay[i]]);
  const SPARKS = [
    ...spark('streak', [0, 45, 90, 135, 180, 225, 270, 315], [26, 23, 27, 24, 26, 22, 27, 24], [40, 70, 50, 80, 40, 70, 50, 80]),
    ...spark('dot', [18, 72, 107, 160, 200, 254, 288, 340], [27, 24, 28, 25, 27, 25, 28, 24], [120, 150, 130, 170, 120, 160, 140, 150]),
    ...spark('glint', [10, 75, 140, 200, 255, 320], [18, 21, 17, 20, 19, 22], [260, 300, 280, 330, 290, 310]),
  ];
  C.burst = (host, { subtle = false, delay = 0, left } = {}) => {
    if (!host || reduced()) return;
    const burst = document.createElement('span');
    burst.className = subtle ? 'c-burst c-burst--subtle' : 'c-burst';
    burst.setAttribute('aria-hidden', 'true');
    if (left) burst.style.left = left;
    const part = (kind, style) => burst.appendChild(Object.assign(document.createElement('span'), { className: 'c-burst__' + kind })).style.cssText = style;
    part('ring', `animation-delay:${delay}ms`);
    for (const [kind, angle, to, own] of SPARKS)
      if (!subtle || kind !== 'dot') part(kind, `--angle:${angle}deg;--to:${to}px;animation-delay:${own + delay}ms`);
    host.append(burst);
    setTimeout(() => burst.remove(), 1000 + delay); // the last dot ends at 890 ms
  };
  // Each add celebrates on the bag in view (cart-dropdown); the bag sheet opens to confirm it first,
  // so the pop, the firework and the character wait until it closes and are actually seen.
  let celebrating = false;
  const celebrate = () => {
    celebrating = false;
    document.querySelectorAll('.c-header__bag, .c-bag-clip__bag').forEach((bag) => {
      pop(bag.querySelector('.c-bag-glyph'));
      C.burst(bag);
    });
    react('bag');
  };

  /* ---------- Dialogs ---------- */

  // The menu and search triggers say whether their overlay is open (aria-expanded), as the storefront's do.
  const expanded = (id, open) =>
    document.querySelectorAll(`[data-cubica-open="${id}"][aria-expanded]`).forEach((el) => el.setAttribute('aria-expanded', open));
  document.addEventListener('close', (event) => event.target.id && expanded(event.target.id, false), true);
  // Safari and Firefox take a closing dialog out at once (no CSS `overlay` transition), so a phone overlay
  // (FullOverlay) plays its exit while still open ([data-closing] in cubica.css), then closes.
  const nativeExit = CSS.supports('overlay', 'auto');
  const phone = matchMedia('(max-width: 1023px)');
  C.closeDialog = (dialog) => {
    if (!dialog?.open || dialog.hasAttribute('data-closing')) return;
    if (nativeExit || reduced() || !phone.matches || !dialog.classList.contains('c-overlay')) return dialog.close();
    dialog.setAttribute('data-closing', '');
    dialog.closing = setTimeout(() => {
      dialog.removeAttribute('data-closing');
      dialog.close();
    }, 220); // --duration-base
  };
  document.addEventListener(
    'cancel',
    (event) => {
      if (nativeExit || !event.target.classList?.contains('c-overlay')) return;
      event.preventDefault();
      C.closeDialog(event.target);
    },
    true
  );
  C.openDialog = (id, trigger) => {
    const dialog = document.getElementById(id);
    if (!dialog) return;
    // [data-cubica-stack] opens over the others (the market sheet over the phone menu, like nested sheets).
    if (!dialog.hasAttribute('data-cubica-stack'))
      document.querySelectorAll('dialog[open]').forEach((other) => other !== dialog && C.closeDialog(other));
    clearTimeout(dialog.closing);
    dialog.removeAttribute('data-closing');
    // A popover hangs from whichever trigger opened it (its CSS anchor-name).
    const anchor = trigger && getComputedStyle(trigger).anchorName;
    if (anchor && anchor !== 'none') dialog.style.positionAnchor = anchor;
    if (!dialog.open) dialog.showModal();
    expanded(id, true);
    emit('cubica:dialog:open', { id });
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cubica-open]');
    if (opener) {
      event.preventDefault();
      return C.openDialog(opener.dataset.cubicaOpen, opener);
    }
    const closer = event.target.closest('[data-cubica-close]');
    if (closer) return C.closeDialog(closer.closest('dialog'));
    const dialog = event.target;
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      const r = dialog.getBoundingClientRect();
      const outside = event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom;
      if (outside) C.closeDialog(dialog);
    }
  });

  document.addEventListener('change', (event) => {
    const form = event.target.closest('form[data-cubica-autosubmit]');
    if (form) form.submit();
  });

  /* ---------- Bag (Ajax Cart API + Section Rendering API) ---------- */

  const sectionId = 'shopify-section-' + BAG;

  function syncBag(added) {
    const content = document.querySelector('#' + sectionId + ' .c-bag__content');
    if (!content) return null;
    const count = Number(content.dataset.count || 0);
    const products = content.dataset.products.split(',').filter(Boolean);
    const variants = content.dataset.variants.split(',').filter(Boolean);
    const label = count === 1 ? strings.bagOne : String(strings.bagOther || '').replace('[count]', count);
    document.querySelectorAll('[data-cubica-bag-count]').forEach((el) => {
      el.textContent = count;
      el.dataset.count = count;
    });
    document.querySelectorAll('[data-cubica-bag-button]').forEach((el) => el.setAttribute('aria-label', label));
    document
      .querySelectorAll('.c-product-card[data-product-id]')
      .forEach((card) => card.classList.toggle('is-in-bag', products.includes(card.dataset.productId)));
    document
      .querySelectorAll('.c-quick-add [data-variant-id]')
      .forEach((button) => button.classList.toggle('is-in-bag', variants.includes(button.dataset.variantId)));
    if (added) content.querySelector(`.c-bag__line[data-variant-id="${added}"]`)?.classList.add('is-added');
    return { count, products, variants, added: added || null };
  }

  function renderBag(html, added) {
    const host = document.getElementById(sectionId);
    if (html && host) {
      const next = new DOMParser().parseFromString(html, 'text/html').getElementById(sectionId);
      if (next) host.replaceChildren(...next.childNodes);
    }
    const state = syncBag(added);
    if (state) emit('cubica:cart:updated', state);
    return state;
  }

  async function post(url, body) {
    const isForm = body instanceof FormData;
    if (isForm) {
      body.append('sections', BAG);
      body.append('sections_url', location.pathname);
    } else {
      body = JSON.stringify({ ...body, sections: BAG, sections_url: location.pathname });
    }
    const response = await fetch(url, {
      method: 'POST',
      headers: isForm ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = data.description || data.message || strings.addError;
      emit('cubica:cart:error', { message });
      throw new Error(message);
    }
    return data;
  }

  function afterAdd(data, variantId) {
    renderBag(data.sections && data.sections[BAG], String(variantId || data.variant_id || data.items?.[0]?.variant_id || ''));
    C.bagFresh = true;
    C.openDialog(BAG);
    if (document.getElementById(BAG)?.open) celebrating = true;
    else celebrate();
    return data;
  }

  document.getElementById(BAG)?.addEventListener('close', () => celebrating && celebrate());

  C.cart = {
    async get() {
      const response = await fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(strings.addError);
      return response.json();
    },
    async add(variantId, quantity = 1, properties) {
      const item = { id: Number(variantId), quantity };
      if (properties) item.properties = properties;
      return afterAdd(await post(routes.cartAdd + '.js', { items: [item] }), variantId);
    },
    async change(key, quantity) {
      const data = await post(routes.cartChange + '.js', { id: key, quantity });
      renderBag(data.sections && data.sections[BAG]);
      return data;
    },
    async refresh() {
      const root = routes.root.endsWith('/') ? routes.root : routes.root + '/';
      const response = await fetch(`${root}?sections=${BAG}`);
      if (response.ok) renderBag((await response.json())[BAG]);
    },
    // lib/data/cart.ts buyNow: "only" sets the bag aside for after checkout; otherwise this item joins it.
    async buyNow(variantId, only) {
      await restoring;
      const id = Number(variantId);
      const cart = await C.cart.get();
      const add = () => post(routes.cartAdd + '.js', { items: [{ id, quantity: 1 }] });
      if (only && cart.items.some((item) => item.variant_id !== id)) {
        const saved = JSON.stringify({
          at: Date.now(),
          items: cart.items.map(line),
          note: cart.note,
          attributes: cart.attributes,
        });
        storage.set(SAVED, saved);
        if (storage.get(SAVED) !== saved) throw new Error(strings.addError); // nowhere to keep the bag
        await post(routes.cart + '/clear.js', {});
        try {
          await add();
        } catch (error) {
          await restoreSavedBag();
          throw error;
        }
      } else if (!cart.items.some((item) => item.variant_id === id)) await add();
    },
  };

  /* ---------- Saved bag (restoreSavedBag): back in the store after "buy now · only this item" ---------- */

  const SAVED = 'cubica-saved-bag';
  const WEEK = 7 * 24 * 60 * 60 * 1000; // the storefront's saved-bag cookie lifetime
  const line = (item) => {
    const entry = { id: item.variant_id, quantity: item.quantity };
    if (item.properties && Object.keys(item.properties).length) entry.properties = item.properties;
    if (item.selling_plan_allocation) entry.selling_plan = item.selling_plan_allocation.selling_plan.id;
    return entry;
  };
  // Saved lines go back into whatever the cart holds now (an unpaid buy-now item stays; after a paid
  // order it is a fresh cart). A variant in both keeps the saved quantity, as the storefront does.
  // The key goes first, so two pages never restore twice; only a network failure puts it back.
  async function restoreSavedBag() {
    const raw = storage.get(SAVED);
    if (!raw || /\/(checkouts?|thank_you|orders)(\/|$)/.test(location.pathname)) return;
    storage.remove(SAVED);
    let saved;
    try {
      saved = JSON.parse(raw);
    } catch {
      return;
    }
    if (!Array.isArray(saved?.items) || !(Date.now() - saved.at < WEEK)) return;
    try {
      const have = {};
      for (const item of (await C.cart.get()).items) have[item.variant_id] = (have[item.variant_id] || 0) + item.quantity;
      const items = [];
      for (const item of saved.items) {
        const quantity = item.quantity - (have[item.id] || 0);
        have[item.id] = Math.max(0, (have[item.id] || 0) - item.quantity);
        if (quantity > 0) items.push({ ...item, quantity });
      }
      const add = (list) => post(routes.cartAdd + '.js', { items: list });
      // A product that sold out or vanished fails the batch: add the rest one by one.
      if (items.length)
        await add(items).catch((error) =>
          error instanceof TypeError ? Promise.reject(error) : Promise.all(items.map((item) => add([item]).catch((e) => e instanceof TypeError && Promise.reject(e)))),
        );
      if (saved.note || Object.keys(saved.attributes || {}).length)
        await post(routes.cart + '/update.js', { note: saved.note || '', attributes: saved.attributes || {} });
    } catch (error) {
      if (error instanceof TypeError) storage.set(SAVED, raw); // offline: try again on the next page
    }
    await C.cart.refresh().catch(() => {});
  }
  let restoring = restoreSavedBag();
  // Back from checkout through the back/forward cache: no new page load, so restore here.
  addEventListener('pageshow', (event) => event.persisted && (restoring = restoreSavedBag()));

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-cubica-add]');
    if (!form) return;
    event.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-status]');
    const data = new FormData(form);
    submit?.setAttribute('aria-busy', 'true');
    if (status) status.textContent = '';
    try {
      afterAdd(await post(routes.cartAdd + '.js', data), data.get('id'));
    } catch (error) {
      if (status) status.textContent = error.message;
    } finally {
      submit?.removeAttribute('aria-busy');
    }
  });

  class CubicaBag extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', async (event) => {
        const button = event.target.closest('[data-bag-qty]');
        const line = button?.closest('[data-line-key]');
        if (!line || button.getAttribute('aria-busy') === 'true') return;
        button.setAttribute('aria-busy', 'true');
        try {
          await C.cart.change(line.dataset.lineKey, Number(button.dataset.bagQty));
        } catch {
          C.cart.refresh();
        }
      });
      document.addEventListener('cubica:dialog:open', (event) => {
        if (event.detail.id === BAG && !C.bagFresh) C.cart.refresh();
        C.bagFresh = false;
      });
    }
  }

  /* ---------- Favorites (localStorage) ---------- */

  const FAVORITES = 'cubica-favorites';
  C.favorites = {
    list() {
      try {
        return JSON.parse(storage.get(FAVORITES)) || [];
      } catch {
        return [];
      }
    },
    has(handle) {
      return this.list().includes(handle);
    },
    toggle(handle) {
      const handles = new Set(this.list());
      const saved = !handles.delete(handle);
      if (saved) handles.add(handle);
      storage.set(FAVORITES, JSON.stringify([...handles]));
      emit('cubica:favorites:changed', { handles: [...handles] });
      if (saved) react('favorite');
      return saved;
    },
  };

  class CubicaFavorite extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.sync = () => this.button.setAttribute('aria-pressed', C.favorites.has(this.dataset.handle));
      this.button.addEventListener('click', () => {
        if (!C.favorites.toggle(this.dataset.handle)) return; // unsaving stays quiet
        const icon = this.button.querySelector('.c-favorite__icon');
        pop(icon);
        C.burst(icon);
      });
      document.addEventListener('cubica:favorites:changed', this.sync);
      this.sync();
    }
    disconnectedCallback() {
      document.removeEventListener('cubica:favorites:changed', this.sync);
    }
  }

  /* ---------- Product card: gallery and quick add ---------- */

  class CubicaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.c-gallery__track');
      this.dots = [...this.querySelectorAll('.c-gallery__dots span')];
      if (this.dots.length < 2) return;
      this.prev = this.querySelector('[data-step="-1"]');
      this.next = this.querySelector('[data-step="1"]');
      this.track.addEventListener('scroll', () => requestAnimationFrame(() => this.update()), { passive: true });
      this.track.addEventListener('pointerdown', () => (this.browsed = true));
      this.track.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        this.browsed = true;
        this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      });
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-step]');
        if (!step) return;
        this.browsed = true;
        this.go(this.index + Number(step.dataset.step));
      });
      this.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && !this.browsed && this.go(1, true));
      this.addEventListener('pointerleave', (event) => event.pointerType === 'mouse' && !this.browsed && this.go(0, true));
    }
    get index() {
      return Math.round(this.track.scrollLeft / this.track.clientWidth);
    }
    go(index, instant) {
      const target = Math.max(0, Math.min(this.track.children.length - 1, index));
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: instant || reduced() ? 'instant' : 'smooth' });
    }
    show(mediaId) {
      const index = [...this.track.children].findIndex((slide) => slide.dataset.mediaId === mediaId);
      if (index >= 0) this.go(index, true);
    }
    update() {
      const index = this.index;
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index));
      this.prev.disabled = index === 0;
      this.next.disabled = index === this.dots.length - 1;
    }
  }

  let productScript;
  const quickCache = new Map();
  const warmProduct = () =>
    (productScript ??= customElements.get('cubica-product-form')
      ? Promise.resolve()
      : new Promise((ok, ko) =>
          document.head.append(Object.assign(document.createElement('script'), { src: document.getElementById('cubica-quick-add').dataset.script, onload: ok, onerror: ko }))
        ));
  const buyBox = (url) => {
    if (!quickCache.has(url)) {
      const request = fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=cubica-main-product`).then((response) => (response.ok ? response.text() : Promise.reject(response)));
      request.catch(() => quickCache.delete(url));
      quickCache.set(url, request);
    }
    return quickCache.get(url);
  };
  const prefetchQuick = (event) => {
    const target = event.target.closest?.('[data-quick-buy], #cubica-quick-add a.c-pdp__color');
    if (!target) return;
    buyBox(target.dataset.url || target.href).catch(() => {});
    warmProduct().catch(() => {});
  };
  ['pointerdown', 'pointerover', 'focusin'].forEach((type) => document.addEventListener(type, prefetchQuick, { passive: true }));
  if (!navigator.connection?.saveData) {
    const dwell = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          clearTimeout(entry.target.dwell);
          if (!entry.isIntersecting || quickCache.size >= 8) return;
          entry.target.dwell = setTimeout(() => {
            dwell.unobserve(entry.target);
            buyBox(entry.target.dataset.url).catch(() => {});
            warmProduct().catch(() => {});
          }, 800);
        }),
      { threshold: 1 }
    );
    document.querySelectorAll('[data-quick-buy]').forEach((button) => dwell.observe(button));
  }

  async function quickBuy(url, trigger) {
    const dialog = document.getElementById('cubica-quick-add');
    const body = dialog.querySelector('[data-quick-body]');
    const title = dialog.querySelector('[data-quick-title]');
    const wide = matchMedia('(min-width: 1024px)').matches;
    const ready = Promise.all([buyBox(url), warmProduct()]);
    dialog.classList.toggle('c-sheet--right', wide);
    dialog.classList.toggle('c-sheet--bottom', !wide);
    dialog.querySelector('[data-quick-link]').href = url;
    if (!dialog.open) title.textContent = trigger.closest('.c-product-card')?.querySelector('.c-product-card__title')?.textContent.trim() ?? '';
    const fill = (html) => {
      const form = new DOMParser().parseFromString(html, 'text/html').querySelector('cubica-product-form');
      title.textContent = form.querySelector('.c-pdp__info h1')?.textContent.trim() ?? title.textContent;
      form.querySelectorAll('.c-pdp__gallery, .c-pdp__info, .c-pdp__details, .c-pdp__bar, dialog, .c-pdp__delivery, .c-pdp__guide-link, [data-buy-now], [data-buy-now-live], [data-buy-now-error]').forEach((node) => node.remove());
      body.replaceChildren(form);
    };
    body.onclick = (event) => {
      const color = event.target.closest('a.c-pdp__color');
      if (!color) return;
      event.preventDefault();
      quickBuy(color.href, trigger);
    };
    trigger.setAttribute('aria-busy', 'true');
    const fast = await Promise.race([ready.then(() => true, () => true), new Promise((done) => setTimeout(done, 350, false))]);
    trigger.removeAttribute('aria-busy');
    try {
      if (fast) fill((await ready)[0]);
      else body.replaceChildren(dialog.querySelector('[data-quick-skeleton]').content.cloneNode(true));
      if (!dialog.open) C.openDialog('cubica-quick-add', trigger);
      if (!fast) fill((await ready)[0]);
    } catch {
      location.href = url;
    }
  }

  class CubicaQuickAdd extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.c-quick-add');
      this.status = this.querySelector('[data-status]');
      this.addEventListener('change', (event) => {
        if (event.target.name?.startsWith('color-')) this.pick(event.target);
      });
      this.addEventListener('click', (event) => {
        const quick = event.target.closest('[data-quick-buy]');
        if (quick) return quickBuy(quick.dataset.url, quick);
        const add = event.target.closest('[data-add]');
        if (add) this.add(add);
      });
    }
    pick(input) {
      this.querySelectorAll('.c-quick-add [data-color]').forEach((button) => (button.hidden = button.dataset.color !== input.value));
      const name = this.querySelector('[data-color-name]');
      if (name) name.textContent = input.value;
      if (input.dataset.mediaId) this.querySelector('cubica-gallery')?.show?.(input.dataset.mediaId);
    }
    async add(button) {
      if (button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
      if (this.status) this.status.textContent = '';
      try {
        await C.cart.add(button.dataset.variantId, 1);
      } catch (error) {
        if (this.status) this.status.textContent = error.message;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }
  }

  /* ---------- Header: mega menus and account menu (Radix NavigationMenu / DropdownMenu behavior) ---------- */

  // Radix FocusGroup: arrows move to the next or previous item without wrapping, Home/End to the ends.
  const ARROWS = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'];
  const focusGroup = (event, items) => {
    if (event.defaultPrevented || !['Home', 'End', ...ARROWS].includes(event.key)) return;
    let list = ['ArrowLeft', 'ArrowUp', 'End'].includes(event.key) ? [...items].reverse() : [...items];
    if (ARROWS.includes(event.key)) list = list.slice(list.indexOf(event.target) + 1);
    event.preventDefault();
    list[0]?.focus();
  };

  class CubicaHeader extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-disclosure]').forEach((trigger) => {
        const item = trigger.parentElement;
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        const links = [...panel.querySelectorAll('a')];
        const menu = trigger.dataset.disclosure === 'menu';
        trigger.addEventListener('click', (event) => {
          clearTimeout(this.timer);
          this.toggle(trigger);
          // A keyboard-opened dropdown focuses its first item.
          if (menu && !event.detail && this.current === trigger) links[0]?.focus();
        });
        trigger.addEventListener('keydown', (event) => {
          // ArrowDown enters an open mega menu; on the dropdown ArrowDown/ArrowUp open it at the first/last item.
          const entry = event.key === 'ArrowDown' || (menu && event.key === 'ArrowUp');
          if (!entry || (!menu && this.current !== trigger)) return;
          event.preventDefault();
          this.toggle(trigger, true);
          (event.key === 'ArrowUp' ? links.at(-1) : links[0])?.focus();
        });
        panel.addEventListener('keydown', (event) => focusGroup(event, links));
        if (!menu) {
          item.addEventListener('pointerenter', (event) => this.hover(event, trigger, true));
          item.addEventListener('pointerleave', (event) => this.hover(event, trigger, false));
        }
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) this.toggle(trigger, false);
        });
      });
      const top = [...this.querySelectorAll('.c-nav__list > li > :is(a, button)')];
      top.forEach((el) => el.addEventListener('keydown', (event) => focusGroup(event, top)));
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.current) return;
        const trigger = this.current;
        this.toggle(trigger, false);
        trigger.focus();
      });
      document.addEventListener('click', (event) => {
        if (this.current && !this.current.parentElement.contains(event.target)) this.toggle(this.current, false);
      });

      // The bag clip (cart-dropdown): hangs from the top edge exactly above the bag once the whole navbar is gone.
      this.clip = this.querySelector('.c-bag-clip');
      const bag = this.querySelector('.c-header__bag');
      const place = () => {
        const right = document.documentElement.clientWidth - bag.getBoundingClientRect().right;
        this.clip.style.right = `max(${right - 4}px, env(safe-area-inset-right))`;
      };
      place();
      addEventListener('resize', place);
      new IntersectionObserver(([entry]) => {
        this.headerGone = !entry.isIntersecting;
        this.syncClip();
      }).observe(this.querySelector('.c-header'));
      document.addEventListener('cubica:cart:updated', () => this.syncClip());

      // CharacterCue kind="hello": once per sign-in and tab session, after the 3D module has loaded.
      const hello = this.dataset.hello && 'pangea-cue:hello:' + this.dataset.hello;
      if (hello)
        document.addEventListener('DOMContentLoaded', () => {
          try {
            if (sessionStorage.getItem(hello)) return;
            sessionStorage.setItem(hello, '1');
          } catch {}
          react('hello');
        });
    }
    syncClip() {
      const count = Number(this.clip.querySelector('[data-cubica-bag-count]').dataset.count || 0);
      const shown = !!this.headerGone && (count > 0 || !!this.clip.querySelector('.c-bag-clip__character'));
      this.clip.querySelector('.c-bag-clip__bag').hidden = !count;
      this.clip.classList.toggle('is-shown', shown);
      this.clip.inert = !shown;
    }
    // Radix timing: open after 200 ms, at once while a menu is open or closed under 300 ms ago; close after 150 ms.
    hover(event, trigger, open) {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this.timer);
      const delay = !open ? 150 : this.current || performance.now() - this.closed < 300 ? 0 : 200;
      this.timer = setTimeout(() => this.toggle(trigger, open), delay);
    }
    toggle(trigger, force) {
      const open = force ?? trigger.getAttribute('aria-expanded') !== 'true';
      if (open && this.current && this.current !== trigger) this.set(this.current, false);
      this.set(trigger, open);
      if (open) this.current = trigger;
      else if (this.current === trigger) {
        this.current = null;
        this.closed = performance.now();
      }
    }
    set(trigger, open) {
      trigger.setAttribute('aria-expanded', open);
      document.getElementById(trigger.getAttribute('aria-controls'))?.classList.toggle('is-open', open);
    }
  }

  /* ---------- Search: predictive results through the Section Rendering API ---------- */

  // The storefront's search panel: recent searches (same key and limit) and popular chips on the
  // left, suggested products before typing, then 4 predictive results.
  const RECENT = 'pangea_recent_searches';
  class CubicaSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="q"]');
      this.results = this.querySelector('[data-results]');
      this.clear = this.querySelector('[data-clear-search]');
      this.renderRecent();
      this.input.addEventListener('input', () => {
        this.clear.hidden = !this.input.value;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.query(), 250);
      });
      // An empty field searches nothing (search/index.tsx: `if (term) seeAll()`).
      this.input.form.addEventListener('submit', (event) => {
        if (!this.input.value.trim()) return event.preventDefault();
        this.remember(this.input.value);
      });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-clear-search]')) {
          this.input.value = '';
          this.clear.hidden = true;
          this.input.focus();
          return this.query();
        }
        if (event.target.closest('[data-clear-recent]')) {
          storage.set(RECENT, '[]');
          return this.renderRecent();
        }
        const chip = event.target.closest('[data-term]');
        if (chip) {
          this.input.value = chip.dataset.term;
          this.clear.hidden = false;
          this.input.focus();
          this.remember(chip.dataset.term);
          return this.query();
        }
        if (event.target.closest('[data-remember]')) this.remember(this.input.value);
      });
      document.addEventListener('cubica:dialog:open', (event) => {
        if (event.detail.id === this.closest('dialog')?.id && !this.input.value && !this.results.childElementCount) this.query();
      });
    }
    recent() {
      try {
        const list = JSON.parse(storage.get(RECENT) || '[]');
        return Array.isArray(list) ? list.filter((item) => typeof item === 'string').slice(0, 5) : [];
      } catch {
        return [];
      }
    }
    remember(term) {
      const clean = term.trim();
      if (!clean) return;
      storage.set(RECENT, JSON.stringify([clean, ...this.recent().filter((item) => item.toLowerCase() !== clean.toLowerCase())].slice(0, 5)));
      this.renderRecent();
    }
    renderRecent() {
      const list = this.recent();
      const template = this.querySelector('template[data-recent-item]');
      this.querySelector('[data-recent-list]').replaceChildren(
        ...list.map((term) => {
          const item = template.content.firstElementChild.cloneNode(true);
          item.querySelector('[data-term]').dataset.term = term;
          item.querySelector('span').textContent = term;
          return item;
        })
      );
      this.querySelector('[data-recent]').hidden = !list.length;
    }
    async query() {
      const terms = this.input.value.trim();
      this.controller?.abort();
      this.controller = new AbortController();
      this.results.setAttribute('aria-busy', 'true');
      try {
        const url = terms
          ? `${routes.predictiveSearch}?q=${encodeURIComponent(terms)}&resources[type]=product&resources[limit]=4&section_id=cubica-predictive-search`
          : `${this.dataset.suggest}?section_id=cubica-predictive-search`;
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = doc.getElementById('shopify-section-cubica-predictive-search');
        this.results.replaceChildren(...(section ? section.childNodes : []));
        syncBag();
      } catch (error) {
        // Before a query the panel only suggests: a failed suggestion stays empty (and is retried on the
        // next open) instead of greeting the shopper with an error.
        if (error.name !== 'AbortError') this.results.textContent = terms ? strings.searchError : '';
      } finally {
        this.results.removeAttribute('aria-busy');
      }
    }
  }

  /* ---------- Footer: appearance switch and desktop columns ---------- */

  const THEME = 'cubica-theme';
  const applyTheme = (theme) => {
    const dark = theme === 'dark' || (theme !== 'light' && matchMedia('(prefers-color-scheme: dark)').matches);
    document.documentElement.classList.toggle('dark', dark);
    document.querySelectorAll('[data-cubica-theme-switch] [data-theme]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.theme === theme);
    });
  };
  applyTheme(storage.get(THEME) || 'auto');
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cubica-theme-switch] [data-theme]');
    if (!button) return;
    storage.set(THEME, button.dataset.theme);
    applyTheme(button.dataset.theme);
  });

  const desktop = matchMedia('(min-width: 1024px)');
  const syncFooter = () => document.querySelectorAll('.c-footer__group').forEach((group) => (group.open = desktop.matches));
  syncFooter();
  desktop.addEventListener('change', syncFooter);
  const footerFloor = document.querySelector('.c-footer__controls');
  if (footerFloor) new IntersectionObserver(([entry]) => footerFloor.toggleAttribute('data-bottom-bar', entry.isIntersecting)).observe(footerFloor);

  /* ---------- Personalization (lib/util/avatar.ts): the header follows the shopper's look ---------- */

  // The personalization page stores cubica-avatar ("character:color"), cubica-tint and cubica-reactions
  // ("off") and emits cubica:avatar:changed; other tabs hear the storage event. Only a signed-in
  // shopper's header has cubes; the header's inline script paints their stored cube and tint first.
  const CHARACTERS = ['classic', 'beanie', 'cap', 'headphones', 'glasses', 'bucket'];
  const COLORS = ['coral', 'apricot', 'butter', 'mint', 'sky', 'lavender', 'rose', 'stone'];
  const TINTS = ['grape', 'orchid', 'lagoon'];
  const html = document.documentElement;
  const headerCubes = () => document.querySelectorAll('cubica-header cubica-character');
  const storedLook = () => {
    const [character, color] = (storage.get('cubica-avatar') || '').split(':');
    return { character, color, tint: storage.get('cubica-tint'), reactions: storage.get('cubica-reactions') !== 'off' };
  };
  const applyLook = ({ character, color, tint, reactions }) => {
    const avatar = CHARACTERS.includes(character) && COLORS.includes(color);
    headerCubes().forEach((cube) => {
      if (avatar) {
        Object.assign(cube.dataset, { character, color });
        cube.querySelector('.c-character__poster rect')?.setAttribute('fill', `rgb(var(--avatar-${color}))`);
      }
      cube.dataset.reactions = reactions;
    });
    if (TINTS.includes(tint)) html.dataset.tint = tint;
    else delete html.dataset.tint;
  };
  document.addEventListener('cubica:avatar:changed', (event) => applyLook(event.detail));
  addEventListener('storage', (event) => {
    if (['cubica-avatar', 'cubica-tint', 'cubica-reactions'].includes(event.key)) applyLook(storedLook());
  });
  // On load only add what Liquid could not know: a guest's tint, and reactions turned off on this device.
  const look = storedLook();
  if (!html.dataset.tint && TINTS.includes(look.tint)) html.dataset.tint = look.tint;
  if (!look.reactions) headerCubes().forEach((cube) => (cube.dataset.reactions = false));

  customElements.define('cubica-bag', CubicaBag);
  customElements.define('cubica-favorite', CubicaFavorite);
  customElements.define('cubica-gallery', CubicaGallery);
  customElements.define('cubica-quick-add', CubicaQuickAdd);
  customElements.define('cubica-header', CubicaHeader);
  customElements.define('cubica-search', CubicaSearch);
  syncBag();
})();
